import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import EmptyState from './EmptyState';
import { ListLayout, useListLayout } from './layout';
import { useListPage } from './ListPage';
import './listPage.css';

/**
 * Πίνακας λίστας (σελίδες «Πίνακας», 14/10/2026). Γραμμές με λεπτή γραμμή ανάμεσα, hover surface-2,
 * επιλεγμένη (selectedKey) brand-soft, κλικ σε ΟΛΗ τη γραμμή (και Enter/Space από το πληκτρολόγιο).
 *
 * Διάταξη από το πλάτος του ίδιου του πίνακα (data-layout, ίδιο μέτρημα με το SettingsSheet):
 *   wide   : όλες οι στήλες
 *   medium : χωρίς τις στήλες hideBelow: 'medium'
 *   narrow : χωρίς καμία στήλη hideBelow, και κάθε γραμμή γίνεται στοίβα «τίτλος (+ τιμή δεξιά) / μία
 *            γραμμή meta με « · »», χωρίς εικονίδια. Οι επικεφαλίδες μένουν για τους αναγνώστες οθόνης.
 *
 * ARIA table με div (όχι <table>): κάθε γραμμή είναι δικό της grid με το ίδιο --lp-cols, ώστε η στοίβα
 * του στενού χώρου να είναι απλή αλλαγή διάταξης και να μην πιάνουν οι γενικοί κανόνες thead/tr του
 * RoleLight.css. Γι' αυτό τα πλάτη είναι κομμάτια grid που δεν εξαρτώνται από το περιεχόμενο.
 */

export type ColumnAlign = 'left' | 'right' | 'center';
export type SortDir = 'asc' | 'desc';
export interface SortState {
  key: string;
  dir: SortDir;
}
/** Θέση της στήλης στη στοίβα του στενού χώρου. */
export type StackRole = 'title' | 'meta' | 'end' | 'none';
export type SortValue = string | number | Date | boolean | null | undefined;

export interface Column<T> {
  /** Μοναδικό ανά πίνακα· με sortable χωρίς sortValue, και το πεδίο της γραμμής που ταξινομείται. */
  key: string;
  header: React.ReactNode;
  render: (row: T) => React.ReactNode;
  /**
   * Κομμάτι grid: '120px' (ή 120), '2fr', 'minmax(160px, 1fr)'. Προεπιλογή 'minmax(0, 1fr)'. Σκέτο 'Nfr'
   * γίνεται 'minmax(0, Nfr)'. ΟΧΙ 'auto'/'max-content': κάθε γραμμή είναι δικό της grid και οι στήλες
   * θα έφευγαν από τη θέση τους.
   */
  width?: string | number;
  align?: ColumnAlign;
  /** 'medium': φαίνεται μόνο στο wide. 'narrow': κρύβεται μόνο στο narrow (και δεν μπαίνει στη στοίβα). */
  hideBelow?: 'medium' | 'narrow';
  sortable?: boolean;
  /** Τιμή ταξινόμησης (αλλιώς row[key]). Κενά (null/undefined/'') πάντα στο τέλος. */
  sortValue?: (row: T) => SortValue;
  /** Στο narrow: 'title' (πρώτη γραμμή), 'end' (δεξιά της, π.χ. ποσό), 'meta' (δεύτερη γραμμή), 'none'.
   *  Προεπιλογή: η πρώτη ορατή στήλη 'title' (αν καμία δεν το δηλώνει), οι άλλες 'meta'. */
  stack?: StackRole;
  /** Τι δείχνει η στήλη στη στοίβα — σύντομο κείμενο μίας γραμμής (αλλιώς το render). */
  stackRender?: (row: T) => React.ReactNode;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string | number;
  /** Κλικ (ή Enter/Space) σε όλη τη γραμμή. Κλικ σε κουμπί/σύνδεσμο/πεδίο μέσα στη γραμμή ΔΕΝ μετρά. */
  onRowClick?: (row: T) => void;
  /** Η γραμμή που φαίνεται στο DetailPanel (aria-current). */
  selectedKey?: string | number | null;
  /** Χωρίς γραμμές: σκελετός. Με γραμμές: μένουν και θαμπώνουν λίγο (π.χ. αναζήτηση όσο γράφεις). */
  loading?: boolean;
  /** Όταν δεν υπάρχει καμία γραμμή (και δεν φορτώνει): <EmptyState …/> ή σκέτο κείμενο. */
  empty: React.ReactNode;
  /** Η γραμμή επικεφαλίδων μένει πάνω όσο κυλά η σελίδα (όχι μέσα σε παράθυρο με δική του κύλιση). */
  stickyHeader?: boolean;
  /** Πιο χαμηλές γραμμές (πολλές εγγραφές, π.χ. ημερολόγιο). */
  dense?: boolean;
  /** Κάτω από τις γραμμές: <Pagination/>, <LoadMore/>, σύνολα. */
  footer?: React.ReactNode;
  /** Όνομα του πίνακα (αλλιώς ο τίτλος της ListPage). */
  ariaLabel?: string;
  /** Όνομα της γραμμής για αναγνώστες οθόνης όταν πατιέται (π.χ. «Κλήση από 697…»). */
  rowLabel?: (row: T) => string;
  /** Επιπλέον κλάση γραμμής — μόνο με tokens (π.χ. 'opacity-60' για ανενεργό). */
  rowClassName?: (row: T) => string | undefined;
  /** Ελεγχόμενη ταξινόμηση: ο πίνακας ΔΕΝ ταξινομεί μόνος του (το κάνει η σελίδα ή ο server). */
  sort?: SortState | null;
  /** Αρχική ταξινόμηση (μη ελεγχόμενη: ταξινομεί ο πίνακας). */
  defaultSort?: SortState | null;
  onSortChange?: (sort: SortState) => void;
  /** Γραμμές σκελετού στη φόρτωση (προεπιλογή 6). */
  skeletonRows?: number;
  testId?: string;
}

const FR_TRACK = /^\d*\.?\d+fr$/;
const trackOf = (width: string | number | undefined): string => {
  if (width === undefined || width === '') return 'minmax(0, 1fr)';
  if (typeof width === 'number') return `${width}px`;
  const w = width.trim();
  return FR_TRACK.test(w) ? `minmax(0, ${w})` : w;
};

/** Ποιες στήλες φαίνονται σε κάθε διάταξη. */
export const columnVisibleIn = (hideBelow: Column<unknown>['hideBelow'], layout: ListLayout): boolean => {
  if (layout === 'wide') return true;
  if (layout === 'medium') return hideBelow !== 'medium';
  return !hideBelow;
};

// Κλικ πάνω σε αυτά ανήκει σε αυτά (κουμπί «Μετάβαση», checkbox, σύνδεσμος), όχι στη γραμμή.
const OWN_CLICK = 'a, button, input, select, textarea, label, summary, [role="button"], [role="link"], [role="checkbox"], '
  + '[role="switch"], [role="menuitem"], [role="tab"], [contenteditable="true"], [data-row-click="ignore"]';

const SKELETON_WIDTHS = ['64%', '46%', '78%', '38%', '56%', '70%'];

const blank = (node: React.ReactNode): boolean => node === null || node === undefined || node === false || node === '';

const cx = (...parts: Array<string | false | null | undefined>): string => parts.filter(Boolean).join(' ');

const alignClass = (align: ColumnAlign | undefined): string | false => (align === 'right' ? 'is-right' : align === 'center' && 'is-center');

const toComparable = (v: SortValue): number | string | null => {
  if (v === null || v === undefined || v === '') return null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v.getTime();
  if (typeof v === 'boolean') return v ? 1 : 0;
  return v;
};

const compareValues = (a: number | string, b: number | string): number => {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), 'el', { sensitivity: 'base', numeric: true });
};

export function sortRows<T>(rows: T[], column: Column<T>, dir: SortDir): T[] {
  const get = column.sortValue ?? ((row: T) => (row as unknown as Record<string, SortValue>)[column.key]);
  const sign = dir === 'asc' ? 1 : -1;
  return rows
    .map((row, index) => ({ row, index, value: toComparable(get(row)) }))
    .sort((a, b) => {
      // Κενά πάντα στο τέλος, όποια κι αν είναι η φορά· ίσα κρατούν τη σειρά του server.
      if (a.value === null || b.value === null) {
        if (a.value === b.value) return a.index - b.index;
        return a.value === null ? 1 : -1;
      }
      return compareValues(a.value, b.value) * sign || a.index - b.index;
    })
    .map(x => x.row);
}

export function DataTable<T>({
  columns, rows, rowKey, onRowClick, selectedKey, loading = false, empty, stickyHeader = false, dense = false, footer,
  ariaLabel, rowLabel, rowClassName, sort, defaultSort = null, onSortChange, skeletonRows = 6, testId = 'data-table',
}: DataTableProps<T>): React.ReactElement {
  const { t } = useTranslation(['listPage']);
  const page = useListPage();
  const rootRef = useRef<HTMLDivElement>(null);
  const layout = useListLayout(rootRef);
  const narrow = layout === 'narrow';
  const [innerSort, setInnerSort] = useState<SortState | null>(defaultSort);
  const controlled = sort !== undefined;
  const sortState = controlled ? sort : innerSort;

  const visible = useMemo(() => columns.filter(c => columnVisibleIn(c.hideBelow, layout)), [columns, layout]);
  const template = visible.map(c => trackOf(c.width)).join(' ');

  const shownRows = useMemo(() => {
    if (controlled || !sortState) return rows;
    const column = columns.find(c => c.key === sortState.key && c.sortable);
    return column ? sortRows(rows, column, sortState.dir) : rows;
  }, [rows, columns, sortState, controlled]);

  // Ρόλοι στη στοίβα: ρητό stack, αλλιώς η πρώτη ορατή στήλη χωρίς ρητό ρόλο είναι ο τίτλος.
  const stackRoles = useMemo(() => {
    const explicitTitle = visible.some(c => c.stack === 'title');
    const firstDefault = visible.find(c => !c.stack);
    return new Map(visible.map(c => [c.key, c.stack ?? (!explicitTitle && c === firstDefault ? 'title' : 'meta')] as const));
  }, [visible]);

  const toggleSort = (column: Column<T>) => {
    const next: SortState = sortState?.key === column.key
      ? { key: column.key, dir: sortState.dir === 'asc' ? 'desc' : 'asc' }
      : { key: column.key, dir: 'asc' };
    if (!controlled) setInnerSort(next);
    onSortChange?.(next);
  };

  const clickable = typeof onRowClick === 'function';

  const onRowMouse = (event: React.MouseEvent<HTMLDivElement>, row: T) => {
    const own = (event.target as HTMLElement).closest?.(OWN_CLICK);
    if (own && own !== event.currentTarget && event.currentTarget.contains(own)) return;
    // Σύρσιμο για να αντιγράψεις ένα τηλέφωνο δεν ανοίγει τη γραμμή.
    const selection = typeof window.getSelection === 'function' ? window.getSelection() : null;
    if (selection && !selection.isCollapsed && selection.anchorNode && event.currentTarget.contains(selection.anchorNode)) return;
    onRowClick?.(row);
  };

  const onRowKey = (event: React.KeyboardEvent<HTMLDivElement>, row: T) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onRowClick?.(row);
    }
  };

  const rowProps = (row: T) => {
    const key = rowKey(row);
    const selected = selectedKey !== undefined && selectedKey !== null && String(selectedKey) === String(key);
    return {
      key: String(key),
      role: 'row',
      'data-key': String(key),
      'aria-current': selected ? ('true' as const) : undefined,
      'aria-label': rowLabel?.(row),
      tabIndex: clickable ? 0 : undefined,
      className: cx('lp-tr', narrow && 'lp-tr-stack', clickable && 'is-clickable', rowClassName?.(row)),
      onClick: clickable ? (e: React.MouseEvent<HTMLDivElement>) => onRowMouse(e, row) : undefined,
      onKeyDown: clickable ? (e: React.KeyboardEvent<HTMLDivElement>) => onRowKey(e, row) : undefined,
    };
  };

  // Στη στοίβα οι επικεφαλίδες δεν φαίνονται: κάθε κελί λέει τη δική του στους αναγνώστες οθόνης.
  const srHeader = (column: Column<T>) => (blank(column.header) ? null : <span className="sr-only">{column.header}: </span>);

  const stackRow = (row: T) => {
    const content = (column: Column<T>) => (column.stackRender ?? column.render)(row);
    const of = (role: StackRole) => visible.filter(c => stackRoles.get(c.key) === role);
    const meta = of('meta').map(c => ({ column: c, node: content(c) })).filter(x => !blank(x.node));
    const { key, ...props } = rowProps(row);
    return (
      <div key={key} {...props}>
        <div className="lp-stack-top">
          {of('title').map(c => <span key={c.key} role="cell" className="lp-stack-title">{srHeader(c)}{content(c)}</span>)}
          {of('end').map(c => <span key={c.key} role="cell" className="lp-stack-end">{srHeader(c)}{content(c)}</span>)}
        </div>
        {meta.length > 0 && (
          <div className="lp-stack-meta">
            {meta.map((x, i) => (
              <React.Fragment key={x.column.key}>
                {i > 0 && <span className="lp-stack-sep" aria-hidden="true">·</span>}
                <span role="cell">{srHeader(x.column)}{x.node}</span>
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
    );
  };

  const gridRow = (row: T) => {
    const { key, ...props } = rowProps(row);
    return (
      <div key={key} {...props}>
        {visible.map(c => <div key={c.key} role="cell" className={cx('lp-td', alignClass(c.align))}>{c.render(row)}</div>)}
      </div>
    );
  };

  const skeleton = Array.from({ length: Math.max(1, skeletonRows) }, (_, i) => (
    <div key={`skeleton-${i}`} role="row" className={cx('lp-tr', 'lp-tr-skel', narrow && 'lp-tr-stack')} aria-hidden="true">
      {narrow ? (
        <>
          <span className="lp-skel" style={{ width: SKELETON_WIDTHS[i % SKELETON_WIDTHS.length] }} />
          <span className="lp-skel lp-skel-sm" style={{ width: SKELETON_WIDTHS[(i + 2) % SKELETON_WIDTHS.length] }} />
        </>
      ) : visible.map((c, j) => (
        <div key={c.key} className={cx('lp-td', alignClass(c.align))}>
          <span className="lp-skel" style={{ width: SKELETON_WIDTHS[(i + j) % SKELETON_WIDTHS.length] }} />
        </div>
      ))}
    </div>
  ));

  const showSkeleton = loading && rows.length === 0;
  const isEmpty = !loading && rows.length === 0;
  const label = ariaLabel ?? page?.title;

  return (
    <div
      ref={rootRef}
      className="lp-table"
      data-layout={layout}
      data-dense={dense ? 'true' : undefined}
      data-sticky={stickyHeader && !narrow ? 'true' : undefined}
      data-busy={loading && rows.length > 0 ? 'true' : undefined}
      data-testid={testId}
      style={{ '--lp-cols': template } as React.CSSProperties}
    >
      {isEmpty ? (
        <div className="lp-table-empty">{typeof empty === 'string' ? <EmptyState title={empty} /> : empty}</div>
      ) : (
        <div role="table" aria-label={label} aria-busy={loading || undefined} className="lp-grid">
          {!narrow && (
            <div role="rowgroup" className="lp-thead">
              <div role="row" className="lp-tr lp-head-row">
                {visible.map(c => {
                  const active = sortState?.key === c.key;
                  const ariaSort = c.sortable ? (active ? (sortState!.dir === 'asc' ? 'ascending' : 'descending') : 'none') : undefined;
                  return (
                    <div key={c.key} role="columnheader" className={cx('lp-th', alignClass(c.align))} aria-sort={ariaSort}>
                      {c.sortable ? (
                        <button type="button" className="lp-sort" onClick={() => toggleSort(c)} data-testid={`${testId}-sort-${c.key}`}>
                          <span className="lp-sort-label">{c.header}</span>
                          {active
                            ? (sortState!.dir === 'asc' ? <ArrowUp className="lp-sort-ico" aria-hidden="true" /> : <ArrowDown className="lp-sort-ico" aria-hidden="true" />)
                            : <ChevronsUpDown className="lp-sort-ico is-idle" aria-hidden="true" />}
                        </button>
                      ) : c.header}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <div role="rowgroup" className="lp-tbody">
            {showSkeleton ? skeleton : shownRows.map(row => (narrow ? stackRow(row) : gridRow(row)))}
          </div>
        </div>
      )}
      {loading && <span className="sr-only" role="status">{t('listPage:loading')}</span>}
      {footer && <div className="lp-table-foot">{footer}</div>}
    </div>
  );
}

export default DataTable;
